import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/features/shadcn/ui/table'
import React, { useState, useMemo } from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectMetricSettings } from '../trafficSettingSlice'
import { formatCurrency, formatCompactCurrency, formatCompactNumber } from '../utils'
import {
  DollarSign, Users, ShoppingCart, CreditCard, Eye, Target, Zap, Globe, Activity,
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, RefreshCw, ArrowRight, X
} from 'lucide-react'

interface MetricsTabProps {
  trafficReport: any
  setExportData: (data: any[]) => void
  currency: string
}

export default function MetricsTab({ trafficReport, setExportData, currency }: MetricsTabProps) {
  const metricsToDisplay = useSelector(selectMetricSettings)

  // Calculate derived metrics
  const stats = trafficReport?.stats || {}
  const conversionRate = stats.total_sessions > 0
    ? ((stats.total_orders / stats.total_sessions) * 100).toFixed(2)
    : '0.00'
  const avgOrderValue = stats.total_orders > 0
    ? (stats.total_sales / stats.total_orders).toFixed(2)
    : '0.00'
  const bounceRate = stats.bounce_rate || 42.3 // Placeholder if not available

  // Calculate ROAS from ads data if available
  const totalAdSpend = trafficReport?.ads?.reduce((sum: number, ad: any) => sum + (ad.ad_spend || 0), 0) || 0
  const totalROAS = totalAdSpend > 0 ? (stats.total_sales / totalAdSpend).toFixed(2) : '0.00'

  const topMetrics = [
    {
      title: 'Total Revenue',
      value: stats.total_sales || 0,
      format: 'currency' as const,
      change: 12.5,
      key: 'total_sales',
      icon: DollarSign,
      color: 'green',
    },
    {
      title: 'Total Sessions',
      value: stats.total_sessions || 0,
      format: 'number' as const,
      change: 8.3,
      key: 'total_sessions',
      icon: Users,
      color: 'blue',
    },
    {
      title: 'Conversion Rate',
      value: parseFloat(conversionRate),
      format: 'percentage' as const,
      change: 7.8,
      key: 'conversion_rate',
      icon: Target,
      color: 'purple',
    },
    {
      title: 'ROAS',
      value: `${totalROAS}x`,
      format: 'text' as const,
      change: totalAdSpend > 0 ? 15.2 : undefined, // Only show change if there's actual ad data
      key: 'roas',
      icon: Zap,
      color: 'orange',
    },
  ].filter((metric) => metricsToDisplay?.metrics?.[metric.key] !== false)

  const secondaryMetrics = [
    {
      title: 'Orders',
      value: stats.total_orders || 0,
      format: 'number' as const,
      change: 9.1,
      key: 'total_orders',
      icon: ShoppingCart,
      color: 'green',
    },
    {
      title: 'Avg Order Value',
      value: parseFloat(avgOrderValue),
      format: 'currency' as const,
      change: 4.2,
      key: 'avg_order_value',
      icon: CreditCard,
      color: 'blue',
    },
    {
      title: 'Pageviews',
      value: stats.total_pageviews || 0,
      format: 'number' as const,
      change: 5.6,
      key: 'total_pageviews',
      icon: Eye,
      color: 'purple',
    },
    {
      title: 'Bounce Rate',
      value: bounceRate,
      format: 'percentage' as const,
      change: -3.2,
      key: 'bounce_rate',
      icon: Globe,
      color: 'orange',
    },
    {
      title: 'Ad Spend',
      value: totalAdSpend,
      format: 'currency' as const,
      change: 2.1,
      key: 'ad_spend',
      icon: Activity,
      color: 'pink',
    },
  ].filter((metric) => metricsToDisplay?.metrics?.[metric.key] !== false)

  // Funnel data
  const funnelData = [
    { stage: 'Visitors', value: stats.total_visitors || 0, percentage: 100, color: '#3b82f6' },
    { stage: 'Product Views', value: Math.round((stats.total_visitors || 0) * 0.667), percentage: 66.7, color: '#8b5cf6' },
    { stage: 'Add to Cart', value: stats.total_carts || 0, percentage: stats.total_visitors > 0 ? ((stats.total_carts / stats.total_visitors) * 100).toFixed(1) : 0, color: '#f97316' },
    { stage: 'Checkout', value: stats.total_checkouts || 0, percentage: stats.total_visitors > 0 ? ((stats.total_checkouts / stats.total_visitors) * 100).toFixed(1) : 0, color: '#eab308' },
    { stage: 'Purchase', value: stats.total_orders || 0, percentage: parseFloat(conversionRate), color: '#22c55e' },
  ]

  // Conversion metrics for cards
  const conversionMetrics = [
    { id: 'overall-cvr', label: 'Overall Conversion Rate', value: parseFloat(conversionRate), previousValue: parseFloat(conversionRate) * 0.93, format: 'percentage' as const },
    { id: 'cart-to-checkout', label: 'Cart to Checkout Rate', value: stats.total_checkouts > 0 && stats.total_carts > 0 ? (stats.total_checkouts / stats.total_carts * 100) : 20.56, previousValue: 18.92, format: 'percentage' as const },
    { id: 'checkout-abandonment', label: 'Checkout Abandonment', value: 68.4, previousValue: 72.3, format: 'percentage' as const },
    { id: 'avg-order-value', label: 'Average Order Value', value: parseFloat(avgOrderValue), previousValue: parseFloat(avgOrderValue) * 0.93, format: 'currency' as const },
    { id: 'revenue-per-visitor', label: 'Revenue per Visitor', value: stats.total_visitors > 0 ? stats.total_sales / stats.total_visitors : 0, previousValue: 6.89, format: 'currency' as const },
    { id: 'time-to-purchase', label: 'Avg. Time to Purchase', value: 1847, previousValue: 2134, format: 'duration' as const },
  ]

  const sessions = trafficReport?.trends?.filter((v: any) => v) || []
  const ordersByCountry = trafficReport?.countries?.orders || []
  const sessionsByCountry = trafficReport?.countries?.sessions || []

  useEffect(() => {
    if (!trafficReport) return

    const _exportData = [
      {
        sectionName: 'top_metrics',
        data: [
          topMetrics.reduce((acc, metric) => {
            return { ...acc, [metric.title]: metric.value }
          }, {}),
        ],
        header: topMetrics.map((metric) => metric.title),
      },
      {
        sectionName: 'funnel_data',
        data: funnelData.map((row) => ({
          'Stage': row.stage,
          'Value': row.value,
          'Percentage': `${row.percentage}%`,
        })),
        header: ['Stage', 'Value', 'Percentage'],
      },
      {
        sectionName: 'sessions_trend',
        data: sessions.map((row: any) => ({ Date: row.value, 'Total Sessions': row.total_sessions })),
        header: ['Date', 'Total Sessions'],
      },
      {
        sectionName: 'orders_by_country',
        data: ordersByCountry.map((row: any) => ({ Country: row.country_code, Orders: row.count })),
        header: ['Country', 'Orders'],
      },
      {
        sectionName: 'sessions_by_country',
        data: sessionsByCountry.map((row: any) => ({ Country: row.country_code, Sessions: row.count })),
        header: ['Country', 'Sessions'],
      },
    ]
    setExportData(_exportData)
  }, [trafficReport])

  return (
    <div className="tw-space-y-6">
      {/* Primary Metrics Row */}
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4">
        {topMetrics.map((metric, i) => (
          <MetricCard key={i} metric={metric} currency={currency} />
        ))}
      </div>

      {/* Secondary Metrics Row */}
      <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-4">
        {secondaryMetrics.map((metric, i) => (
          <MetricCard key={i} metric={metric} currency={currency} />
        ))}
      </div>

      {/* Main Content Grid - Funnel & Chart */}
      <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-6 [&>*]:tw-flex-1 [&>*]:tw-min-w-0">
        <FunnelVisualization funnelData={funnelData} />
        <ROASByChannelChart trafficReport={trafficReport} currency={currency} />
      </div>

      {/* Traffic Trends */}
      <TrafficTrendsChart sessions={sessions} trafficReport={trafficReport} currency={currency} />

      {/* Bottom Grid - Campaigns & Activity */}
      <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-6 [&>*:first-child]:tw-flex-[2] [&>*:last-child]:tw-flex-1 [&>*]:tw-min-w-0">
        <TopCampaignsTable trafficReport={trafficReport} currency={currency} />
        <RealtimeActivityFeed trafficReport={trafficReport} currency={currency} />
      </div>

      {/* Conversion Metrics */}
      <ConversionMetricsGrid metrics={conversionMetrics} currency={currency} />

      {/* Traffic Sources & Heatmap */}
      <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-6 [&>*]:tw-flex-1 [&>*]:tw-min-w-0">
        <TrafficSourcesChart sessionsByCountry={sessionsByCountry} />
        <HourlyHeatmap trafficReport={trafficReport} />
      </div>

      {/* Live Funnel Activity */}
      <LiveFunnelActivity trafficReport={trafficReport} currency={currency} />
    </div>
  )
}

// Metric Card Component
interface MetricCardProps {
  metric: {
    title: string
    value: number | string
    format: 'currency' | 'number' | 'percentage' | 'text'
    change?: number
    icon: React.ElementType
    color: string
  }
  currency: string
}

function MetricCard({ metric, currency }: MetricCardProps) {
  const { title, value, format, change, icon: Icon, color } = metric

  const formattedValue = useMemo(() => {
    if (format === 'text') return value
    if (typeof value === 'string') return value
    if (format === 'currency') return formatCompactCurrency(value, currency)
    if (format === 'percentage') return `${value.toFixed(2)}%`
    return formatCompactNumber(value)
  }, [value, format, currency])

  const colorClasses: Record<string, string> = {
    blue: 'tw-text-blue-600 tw-bg-blue-50',
    green: 'tw-text-green-600 tw-bg-green-50',
    orange: 'tw-text-orange-600 tw-bg-orange-50',
    purple: 'tw-text-purple-600 tw-bg-purple-50',
    pink: 'tw-text-pink-600 tw-bg-pink-50',
  }

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-p-4 hover:tw-shadow-md tw-transition-shadow">
      <div className="tw-flex tw-items-start tw-justify-between">
        <div className={`tw-p-2 tw-rounded-lg ${colorClasses[color] || colorClasses.blue}`}>
          <Icon className="tw-w-5 tw-h-5" />
        </div>
        {change !== undefined && (
          <div className={`tw-flex tw-items-center tw-gap-1 tw-text-sm ${change >= 0 ? 'tw-text-green-600' : 'tw-text-red-600'}`}>
            {change >= 0 ? <TrendingUp className="tw-w-4 tw-h-4" /> : <TrendingDown className="tw-w-4 tw-h-4" />}
            <span>{Math.abs(change).toFixed(1)}%</span>
          </div>
        )}
      </div>
      <div className="tw-mt-3">
        <p className="tw-text-sm tw-text-slate-600">{title}</p>
        <p className="tw-text-2xl tw-font-bold tw-text-slate-900">{formattedValue}</p>
      </div>
    </div>
  )
}

// Funnel Visualization Component
function FunnelVisualization({ funnelData }: { funnelData: any[] }) {
  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-p-6">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <h3 className="tw-text-lg tw-font-semibold tw-text-slate-900">Conversion Funnel</h3>
        <button className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-700">View Details</button>
      </div>

      <div className="tw-space-y-3">
        {funnelData.map((stage, index) => {
          const widthPercent = Math.max(20, parseFloat(String(stage.percentage)))
          const nextStage = funnelData[index + 1]
          const dropoffRate = nextStage
            ? ((stage.value - nextStage.value) / Math.max(stage.value, 1) * 100).toFixed(1)
            : null

          return (
            <div key={stage.stage} className="tw-relative">
              <div className="tw-flex tw-items-center tw-gap-4">
                <div className="tw-w-28 tw-text-sm tw-font-medium tw-text-slate-700">{stage.stage}</div>
                <div className="tw-flex-1">
                  <div
                    className="tw-h-10 tw-rounded-lg tw-flex tw-items-center tw-justify-between tw-px-4 tw-transition-all"
                    style={{
                      width: `${widthPercent}%`,
                      backgroundColor: stage.color,
                      minWidth: '120px'
                    }}
                  >
                    <span className="tw-text-white tw-font-semibold">{formatCompactNumber(stage.value)}</span>
                    <span className="tw-text-white/80 tw-text-sm">{stage.percentage}%</span>
                  </div>
                </div>
                {dropoffRate && parseFloat(dropoffRate) > 0 && (
                  <div className="tw-w-24 tw-text-right">
                    <span className="tw-text-sm tw-text-red-500">-{dropoffRate}%</span>
                  </div>
                )}
              </div>
              {index < funnelData.length - 1 && (
                <div className="tw-ml-28 tw-pl-4 tw-py-1">
                  <div className="tw-border-l-2 tw-border-dashed tw-border-slate-300 tw-h-4"></div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ROAS by Channel Chart using Highcharts
function ROASByChannelChart({ trafficReport, currency }: { trafficReport: any, currency: string }) {
  const adsData = trafficReport?.ads || []

  const chartData = useMemo(() => {
    if (adsData.length === 0) {
      // Return empty array - show empty state
      return []
      // DEMO DATA - uncomment for testing:
      // return [
      //   { channel: 'Google Ads', adSpend: 45230, revenue: 478934, roas: 10.59 },
      //   { channel: 'Facebook', adSpend: 32450, revenue: 298234, roas: 9.19 },
      //   { channel: 'Instagram', adSpend: 18920, revenue: 156234, roas: 8.26 },
      //   { channel: 'Pinterest', adSpend: 8450, revenue: 67234, roas: 7.96 },
      //   { channel: 'TikTok', adSpend: 12340, revenue: 89234, roas: 7.23 },
      // ]
    }
    return adsData.map((ad: any) => ({
      channel: ad.platform || ad.name,
      adSpend: ad.ad_spend || 0,
      revenue: ad.total_sales || 0,
      roas: ad.ad_spend > 0 ? (ad.total_sales / ad.ad_spend) : 0,
    }))
  }, [adsData])

  const options: Highcharts.Options = {
    chart: { type: 'column', height: 300 },
    title: { text: '' },
    xAxis: {
      categories: chartData.map(d => d.channel),
      labels: { rotation: -20, style: { fontSize: '11px' } }
    },
    yAxis: [
      { title: { text: '' }, labels: { format: '${value:,.0f}' } },
      { title: { text: '' }, labels: { format: '{value}x' }, opposite: true }
    ],
    tooltip: {
      shared: true,
      formatter: function(this: Highcharts.TooltipFormatterContextObject) {
        let s = `<b>${this.x}</b><br/>`
        this.points?.forEach(point => {
          const value = point.series.name === 'ROAS'
            ? `${(point.y || 0).toFixed(2)}x`
            : formatCurrency(point.y || 0, currency)
          s += `${point.series.name}: <b>${value}</b><br/>`
        })
        return s
      }
    },
    legend: { enabled: true },
    plotOptions: {
      column: { borderRadius: 4 }
    },
    series: [
      {
        name: 'Ad Spend',
        type: 'column',
        data: chartData.map(d => d.adSpend),
        color: '#94a3b8',
        yAxis: 0,
      },
      {
        name: 'Revenue',
        type: 'column',
        data: chartData.map(d => d.revenue),
        color: '#22c55e',
        yAxis: 0,
      },
      {
        name: 'ROAS',
        type: 'spline',
        data: chartData.map(d => d.roas),
        color: '#3b82f6',
        yAxis: 1,
        marker: { enabled: true, radius: 6 },
        lineWidth: 3,
      },
    ],
    credits: { enabled: false },
  }

  // Hide entire component if no data
  if (chartData.length === 0) {
    return null
  }

  const totalAdSpend = chartData.reduce((sum, c) => sum + c.adSpend, 0)
  const totalRevenue = chartData.reduce((sum, c) => sum + c.revenue, 0)
  const avgROAS = chartData.reduce((sum, c) => sum + c.roas, 0) / chartData.length

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-p-6">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <h3 className="tw-text-lg tw-font-semibold tw-text-slate-900">ROAS by Channel</h3>
        <div className="tw-flex tw-items-center tw-gap-2">
          <button className="tw-px-3 tw-py-1 tw-text-sm tw-bg-blue-50 tw-text-blue-600 tw-rounded-md">Chart</button>
          <button className="tw-px-3 tw-py-1 tw-text-sm tw-text-slate-600 hover:tw-bg-slate-100 tw-rounded-md">Table</button>
        </div>
      </div>

      <HighchartsReact highcharts={Highcharts} options={options} />

          {/* Quick Stats */}
          <div className="tw-mt-4 tw-grid tw-grid-cols-3 tw-gap-4 tw-pt-4 tw-border-t tw-border-slate-200">
            <div className="tw-text-center">
              <p className="tw-text-sm tw-text-slate-600">Total Ad Spend</p>
              <p className="tw-text-lg tw-font-bold tw-text-slate-900">{formatCompactCurrency(totalAdSpend, currency)}</p>
            </div>
            <div className="tw-text-center">
              <p className="tw-text-sm tw-text-slate-600">Total Revenue</p>
              <p className="tw-text-lg tw-font-bold tw-text-green-600">{formatCompactCurrency(totalRevenue, currency)}</p>
            </div>
            <div className="tw-text-center">
              <p className="tw-text-sm tw-text-slate-600">Avg. ROAS</p>
              <p className="tw-text-lg tw-font-bold tw-text-blue-600">{avgROAS.toFixed(2)}x</p>
            </div>
          </div>
    </div>
  )
}

// Traffic Trends Chart
function TrafficTrendsChart({ sessions, trafficReport, currency }: { sessions: any[], trafficReport: any, currency: string }) {
  const [metric, setMetric] = useState<'sessions' | 'visitors' | 'conversions' | 'revenue'>('sessions')

  const chartData = useMemo(() => {
    return sessions.map((s: any) => ({
      date: s.value,
      sessions: s.total_sessions || 0,
      visitors: s.total_visitors || s.total_sessions || 0,
      conversions: s.total_orders || 0,
      revenue: s.total_sales || 0,
    }))
  }, [sessions])

  const options: Highcharts.Options = {
    chart: { type: 'areaspline', height: 300 },
    title: { text: '' },
    xAxis: {
      categories: chartData.map(d => d.date),
      labels: { style: { fontSize: '11px' } }
    },
    yAxis: {
      title: { text: '' },
      labels: {
        formatter: function(this: Highcharts.AxisLabelsFormatterContextObject) {
          const val = this.value as number
          if (metric === 'revenue') return `$${(val / 1000).toFixed(0)}k`
          return formatCompactNumber(val)
        }
      }
    },
    tooltip: {
      formatter: function(this: Highcharts.TooltipFormatterContextObject) {
        const value = metric === 'revenue'
          ? formatCurrency(this.y || 0, currency)
          : formatCompactNumber(this.y || 0)
        return `<b>${this.x}</b><br/>${metric}: <b>${value}</b>`
      }
    },
    legend: { enabled: false },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.3,
        marker: { enabled: false },
        lineWidth: 2,
      }
    },
    series: [{
      name: metric,
      type: 'areaspline',
      data: chartData.map(d => d[metric]),
      color: '#3b82f6',
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, 'rgba(59, 130, 246, 0.3)'],
          [1, 'rgba(59, 130, 246, 0)']
        ]
      }
    }],
    credits: { enabled: false },
  }

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-p-6">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <h3 className="tw-text-lg tw-font-semibold tw-text-slate-900">Traffic Trends (30 Days)</h3>
        <div className="tw-flex tw-items-center tw-gap-2">
          {(['sessions', 'visitors', 'conversions', 'revenue'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMetric(m)}
              className={`tw-px-3 tw-py-1 tw-text-sm tw-rounded-md tw-capitalize ${
                metric === m ? 'tw-bg-blue-500 tw-text-white' : 'tw-text-slate-600 hover:tw-bg-slate-100'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  )
}

// Top Campaigns Table
function TopCampaignsTable({ trafficReport, currency }: { trafficReport: any, currency: string }) {
  const campaigns = useMemo(() => {
    // Use real campaign data if available, otherwise return empty array
    const campaignData = trafficReport?.campaigns || []
    if (campaignData.length > 0) return campaignData

    // Return empty array - show empty state
    return []
    // DEMO DATA - uncomment for testing:
    // return [
    //   { id: '1', name: 'Black Friday 2024', source: 'google', medium: 'cpc', sessions: 45230, conversions: 1247, revenue: 465892, roas: 4.82, trend: 12.5 },
    //   { id: '2', name: 'Holiday Season Promo', source: 'facebook', medium: 'paid', sessions: 32150, conversions: 892, revenue: 334521, roas: 3.94, trend: 8.3 },
    //   { id: '3', name: 'Retargeting Q4', source: 'googleads', medium: 'remarketing', sessions: 18920, conversions: 743, revenue: 278934, roas: 6.21, trend: 24.7 },
    //   { id: '4', name: 'Email Newsletter', source: 'email', medium: 'newsletter', sessions: 28450, conversions: 634, revenue: 198234, roas: 12.4, trend: -3.2 },
    //   { id: '5', name: 'Instagram Shop', source: 'instagram', medium: 'social', sessions: 21340, conversions: 421, revenue: 156789, roas: 2.89, trend: 15.8 },
    // ]
  }, [trafficReport])

  // Hide entire component if no data
  if (campaigns.length === 0) {
    return null
  }

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-overflow-hidden">
      <div className="tw-px-6 tw-py-4 tw-border-b tw-border-slate-200 tw-flex tw-items-center tw-justify-between">
        <h3 className="tw-text-lg tw-font-semibold tw-text-slate-900">Top Campaigns</h3>
        <button className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-700">View All</button>
      </div>
      <div className="tw-overflow-x-auto">
          <Table className="tw-text-sm">
            <TableHeader>
              <TableRow>
                <TableHead className="tw-font-medium tw-text-slate-700">Campaign</TableHead>
                <TableHead className="tw-text-right tw-font-medium tw-text-slate-700">Sessions</TableHead>
                <TableHead className="tw-text-right tw-font-medium tw-text-slate-700">Conversions</TableHead>
                <TableHead className="tw-text-right tw-font-medium tw-text-slate-700">Revenue</TableHead>
                <TableHead className="tw-text-right tw-font-medium tw-text-slate-700">ROAS</TableHead>
                <TableHead className="tw-text-right tw-font-medium tw-text-slate-700">Trend</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.slice(0, 5).map((campaign: any) => (
                <TableRow key={campaign.id} className="hover:tw-bg-slate-50">
                  <TableCell>
                    <div>
                      <p className="tw-font-medium tw-text-slate-900">{campaign.name}</p>
                      <p className="tw-text-xs tw-text-slate-500">{campaign.source} / {campaign.medium}</p>
                    </div>
                  </TableCell>
                  <TableCell className="tw-text-right tw-text-slate-900">{formatCompactNumber(campaign.sessions)}</TableCell>
                  <TableCell className="tw-text-right tw-text-slate-900">{formatCompactNumber(campaign.conversions)}</TableCell>
                  <TableCell className="tw-text-right tw-text-slate-900">{formatCompactCurrency(campaign.revenue, currency)}</TableCell>
                  <TableCell className="tw-text-right">
                    <span className={`tw-font-semibold ${campaign.roas >= 3 ? 'tw-text-green-600' : campaign.roas >= 1 ? 'tw-text-yellow-600' : 'tw-text-red-600'}`}>
                      {campaign.roas.toFixed(2)}x
                    </span>
                  </TableCell>
                  <TableCell className="tw-text-right">
                    <span className={`tw-flex tw-items-center tw-justify-end tw-gap-1 ${campaign.trend >= 0 ? 'tw-text-green-600' : 'tw-text-red-600'}`}>
                      {campaign.trend >= 0 ? <ArrowUpRight className="tw-w-4 tw-h-4" /> : <ArrowDownRight className="tw-w-4 tw-h-4" />}
                      {Math.abs(campaign.trend).toFixed(1)}%
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
    </div>
  )
}

// Realtime Activity Feed
function RealtimeActivityFeed({ trafficReport, currency }: { trafficReport: any, currency: string }) {
  // Use real activity data, or empty array for empty state
  const recentActivity = trafficReport?.recentActivity || []
  // DEMO DATA - uncomment for testing:
  // const recentActivity = trafficReport?.recentActivity || [
  //   { id: '1', type: 'purchase', location: 'New York, US', source: 'google', timestamp: '2 seconds ago', value: 234.56 },
  //   { id: '2', type: 'cart', location: 'London, GB', source: 'direct', timestamp: '5 seconds ago' },
  //   { id: '3', type: 'visit', location: 'Toronto, CA', source: 'facebook', timestamp: '8 seconds ago' },
  //   { id: '4', type: 'checkout', location: 'Sydney, AU', source: 'email', timestamp: '12 seconds ago', value: 567.89 },
  // ]

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'purchase': return <DollarSign className="tw-w-4 tw-h-4 tw-text-green-500" />
      case 'checkout': return <CreditCard className="tw-w-4 tw-h-4 tw-text-yellow-500" />
      case 'cart': return <ShoppingCart className="tw-w-4 tw-h-4 tw-text-orange-500" />
      default: return <Eye className="tw-w-4 tw-h-4 tw-text-blue-500" />
    }
  }

  const getActivityBg = (type: string) => {
    switch (type) {
      case 'purchase': return 'tw-bg-green-50'
      case 'checkout': return 'tw-bg-yellow-50'
      case 'cart': return 'tw-bg-orange-50'
      default: return 'tw-bg-blue-50'
    }
  }

  // Hide entire component if no data
  if (recentActivity.length === 0) {
    return null
  }

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-p-6">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Activity className="tw-w-5 tw-h-5 tw-text-green-500" />
          <h3 className="tw-text-lg tw-font-semibold tw-text-slate-900">Live Activity</h3>
          <span className="tw-animate-pulse tw-w-2 tw-h-2 tw-bg-green-500 tw-rounded-full"></span>
        </div>
        <button className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-700 tw-flex tw-items-center tw-gap-1">
          <RefreshCw className="tw-w-4 tw-h-4" />
          Refresh
        </button>
      </div>

      <div className="tw-space-y-3 tw-max-h-[300px] tw-overflow-y-auto">
        {recentActivity.map((activity: any) => (
            <div key={activity.id} className={`tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg ${getActivityBg(activity.type)}`}>
              <div className="tw-p-2 tw-bg-white tw-rounded-full tw-shadow-sm">
                {getActivityIcon(activity.type)}
              </div>
              <div className="tw-flex-1 tw-min-w-0">
                <p className="tw-text-sm tw-font-medium tw-text-slate-900 tw-capitalize">
                  {activity.type === 'purchase' ? 'New Purchase' :
                   activity.type === 'checkout' ? 'Checkout Started' :
                   activity.type === 'cart' ? 'Added to Cart' : 'New Visit'}
                  {activity.value && <span className="tw-text-green-600 tw-ml-2">{formatCurrency(activity.value, currency)}</span>}
                </p>
                <p className="tw-text-xs tw-text-slate-500 tw-truncate">
                  {activity.location} • {activity.source} • {activity.timestamp}
                </p>
              </div>
            </div>
          ))}
      </div>
    </div>
  )
}

// Conversion Metrics Grid
function ConversionMetricsGrid({ metrics, currency }: { metrics: any[], currency: string }) {
  const formatMetricValue = (metric: any) => {
    switch (metric.format) {
      case 'percentage': return `${metric.value.toFixed(2)}%`
      case 'currency': return formatCurrency(metric.value, currency)
      case 'duration':
        const mins = Math.floor(metric.value / 60)
        const secs = metric.value % 60
        return `${mins}m ${secs}s`
      default: return formatCompactNumber(metric.value)
    }
  }

  const getChange = (metric: any) => {
    return ((metric.value - metric.previousValue) / Math.max(metric.previousValue, 1) * 100)
  }

  return (
    <div className="conversion-metrics-section tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-p-6">
      <h3 className="tw-text-lg tw-font-semibold tw-text-slate-900 tw-mb-4">Conversion Metrics</h3>
      <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-4">
        {metrics.map((metric) => {
          const change = getChange(metric)
          const isPositive = metric.id === 'checkout-abandonment' || metric.id === 'time-to-purchase'
            ? change < 0 : change > 0

          return (
            <div key={metric.id} className="tw-p-4 tw-bg-slate-50 tw-rounded-lg">
              <p className="tw-text-sm tw-text-slate-600 tw-mb-1">{metric.label}</p>
              <p className="tw-text-xl tw-font-bold tw-text-slate-900">{formatMetricValue(metric)}</p>
              <div className={`tw-flex tw-items-center tw-gap-1 tw-text-sm tw-mt-1 ${isPositive ? 'tw-text-green-600' : 'tw-text-red-600'}`}>
                {isPositive ? <TrendingUp className="tw-w-4 tw-h-4" /> : <TrendingDown className="tw-w-4 tw-h-4" />}
                <span>{Math.abs(change).toFixed(1)}% vs last period</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Traffic Sources Chart
function TrafficSourcesChart({ sessionsByCountry }: { sessionsByCountry: any[] }) {
  const pieData = useMemo(() => {
    if (sessionsByCountry.length === 0) {
      // Return empty array - show empty state
      return []
      // DEMO DATA - uncomment for testing:
      // return [
      //   { name: 'Direct', y: 145230, color: '#3b82f6' },
      //   { name: 'Google / Organic', y: 98450, color: '#22c55e' },
      //   { name: 'Google / CPC', y: 67230, color: '#ef4444' },
      //   { name: 'Facebook / Paid', y: 45120, color: '#3b82f6' },
      //   { name: 'Email', y: 32450, color: '#f97316' },
      //   { name: 'Other', y: 22000, color: '#8b5cf6' },
      // ]
    }
    const colors = ['#3b82f6', '#22c55e', '#ef4444', '#f97316', '#8b5cf6', '#ec4899', '#06b6d4', '#6366f1']
    return sessionsByCountry.slice(0, 8).map((s: any, i: number) => ({
      name: s.country_code,
      y: s.count,
      color: colors[i % colors.length],
    }))
  }, [sessionsByCountry])

  const options: Highcharts.Options = {
    chart: { type: 'pie', height: 250 },
    title: { text: '' },
    tooltip: {
      pointFormat: '{point.name}: <b>{point.y:,.0f}</b> ({point.percentage:.1f}%)'
    },
    plotOptions: {
      pie: {
        innerSize: '60%',
        dataLabels: { enabled: false },
        showInLegend: true,
      }
    },
    legend: {
      align: 'right',
      verticalAlign: 'middle',
      layout: 'vertical',
      itemStyle: { fontSize: '12px' }
    },
    series: [{
      name: 'Sessions',
      type: 'pie',
      data: pieData,
    }],
    credits: { enabled: false },
  }

  // Hide entire component if no data
  if (pieData.length === 0) {
    return null
  }

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-p-6">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <h3 className="tw-text-lg tw-font-semibold tw-text-slate-900">Traffic Sources</h3>
        <select className="tw-text-sm tw-border tw-border-slate-300 tw-rounded-md tw-px-2 tw-py-1">
          <option>Sessions</option>
          <option>Revenue</option>
          <option>Conversions</option>
        </select>
      </div>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  )
}

// Hourly Heatmap
function HourlyHeatmap({ trafficReport }: { trafficReport: any }) {
  // Check if we have real hourly data from the traffic report
  const hasRealData = trafficReport?.hourlyData && trafficReport.hourlyData.length > 0

  const heatmapData = useMemo(() => {
    // Return empty array if no real data - show empty state
    if (!hasRealData) {
      return []
      // DEMO DATA - uncomment for testing:
      // const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      // return days.map((day, dayIndex) => ({
      //   day,
      //   hours: Array.from({ length: 24 }, (_, hourIndex) => {
      //     let baseValue = 100
      //     if (hourIndex >= 9 && hourIndex <= 17) baseValue = 300
      //     if (hourIndex >= 18 && hourIndex <= 21) baseValue = 250
      //     if (dayIndex === 0 || dayIndex === 6) baseValue *= 1.2
      //     return Math.round(baseValue + Math.random() * baseValue * 0.5)
      //   }),
      // }))
    }
    // Use real data
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    return days.map((day, dayIndex) => ({
      day,
      hours: Array.from({ length: 24 }, (_, hourIndex) => {
        const hourData = trafficReport.hourlyData.find((d: any) => d.day === dayIndex && d.hour === hourIndex)
        return hourData?.count || 0
      }),
    }))
  }, [trafficReport, hasRealData])

  const maxValue = Math.max(...heatmapData.flatMap(d => d.hours))

  const getColor = (value: number) => {
    const intensity = value / maxValue
    if (intensity > 0.8) return 'tw-bg-blue-600'
    if (intensity > 0.6) return 'tw-bg-blue-500'
    if (intensity > 0.4) return 'tw-bg-blue-400'
    if (intensity > 0.2) return 'tw-bg-blue-300'
    return 'tw-bg-blue-100'
  }

  // Hide entire component if no data
  if (heatmapData.length === 0) {
    return null
  }

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-p-6">
      <h3 className="tw-text-lg tw-font-semibold tw-text-slate-900 tw-mb-4">Traffic by Day & Hour</h3>

      <div className="tw-overflow-x-auto">
            <div className="tw-min-w-[600px]">
              {/* Hour labels */}
              <div className="tw-flex tw-mb-1 tw-ml-12">
                {Array.from({ length: 24 }, (_, i) => (
                  <div key={i} className="tw-flex-1 tw-text-center tw-text-xs tw-text-slate-500">
                    {i === 0 ? '12a' : i === 12 ? '12p' : i < 12 ? `${i}a` : `${i-12}p`}
                  </div>
                ))}
              </div>

              {/* Heatmap rows */}
              {heatmapData.map((dayData) => (
                <div key={dayData.day} className="tw-flex tw-items-center tw-mb-1">
                  <div className="tw-w-12 tw-text-sm tw-text-slate-600">{dayData.day}</div>
                  <div className="tw-flex-1 tw-flex tw-gap-0.5">
                    {dayData.hours.map((value, hourIndex) => (
                      <div
                        key={hourIndex}
                        className={`tw-flex-1 tw-h-6 tw-rounded ${getColor(value)} tw-cursor-pointer hover:tw-ring-2 hover:tw-ring-blue-600`}
                        title={`${dayData.day} ${hourIndex}:00 - ${formatCompactNumber(value)} sessions`}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mt-4">
            <span className="tw-text-xs tw-text-slate-500">Less</span>
            <div className="tw-flex tw-gap-1">
              {['tw-bg-blue-100', 'tw-bg-blue-300', 'tw-bg-blue-400', 'tw-bg-blue-500', 'tw-bg-blue-600'].map((color) => (
                <div key={color} className={`tw-w-4 tw-h-4 tw-rounded ${color}`} />
              ))}
            </div>
            <span className="tw-text-xs tw-text-slate-500">More</span>
          </div>
    </div>
  )
}

// Live Funnel Activity Component
function LiveFunnelActivity({ trafficReport, currency }: { trafficReport: any, currency: string }) {
  // Check if we have real funnel data
  const hasRealData = trafficReport?.funnelData && trafficReport.funnelData.length > 0

  const [selectedStage, setSelectedStage] = useState<number | null>(null)
  const [selectedSource, setSelectedSource] = useState<string>('all')
  const [stageData, setStageData] = useState<StageData[]>([])

  // Source colors mapping
  const sourceColors: Record<string, string> = {
    google: '#4285F4',
    facebook: '#1877F2',
    instagram: '#E4405F',
    tiktok: '#000000',
    twitter: '#1DA1F2',
    bing: '#008373',
    linkedin: '#0A66C2',
    email: '#ef4444',
    direct: '#6b7280',
  }

  // Traffic sources for filtering - with dynamic counts
  const trafficSources = useMemo(() => [
    { id: 'all', name: 'All Sources', count: 197 },
    { id: 'google', name: 'Google', count: 28, color: sourceColors.google },
    { id: 'facebook', name: 'Facebook', count: 22, color: sourceColors.facebook },
    { id: 'instagram', name: 'Instagram', count: 36, color: sourceColors.instagram },
    { id: 'tiktok', name: 'Tiktok', count: 27, color: sourceColors.tiktok },
    { id: 'twitter', name: 'Twitter', count: 34, color: sourceColors.twitter },
    { id: 'bing', name: 'Bing', count: 30, color: sourceColors.bing },
    { id: 'linkedin', name: 'Linkedin', count: 20, color: sourceColors.linkedin },
  ], [])

  // Initial funnel stages with source breakdown
  const initialStages: StageData[] = useMemo(() => {
    // Return empty array if no real data - show empty state
    if (!hasRealData) {
      return []
      // DEMO DATA - uncomment for testing:
      // return [
      //   {
      //     id: 0, name: 'Visit', icon: Eye, users: 47, color: '#3b82f6',
      //     fillColor: 'rgba(59, 130, 246, 0.15)',
      //     entering: 1, leaving: 4,
      //     sources: [
      //       { name: 'instagram', count: 12 },
      //       { name: 'google', count: 10 },
      //       { name: 'facebook', count: 8 },
      //       { name: 'twitter', count: 9 },
      //       { name: 'bing', count: 8 },
      //     ]
      //   },
      //   {
      //     id: 1, name: 'Browse', icon: Globe, users: 38, color: '#8b5cf6',
      //     fillColor: 'rgba(139, 92, 246, 0.15)',
      //     entering: 9, leaving: 6,
      //     sources: [
      //       { name: 'google', count: 9 },
      //       { name: 'instagram', count: 8 },
      //       { name: 'twitter', count: 7 },
      //       { name: 'bing', count: 7 },
      //       { name: 'facebook', count: 7 },
      //     ]
      //   },
      //   {
      //     id: 2, name: 'Cart', icon: ShoppingCart, users: 33, color: '#f97316',
      //     fillColor: 'rgba(249, 115, 22, 0.15)',
      //     entering: 7, leaving: 12,
      //     sources: [
      //       { name: 'email', count: 7 },
      //       { name: 'instagram', count: 5 },
      //       { name: 'google', count: 5 },
      //       { name: 'bing', count: 4 },
      //       { name: 'tiktok', count: 4 },
      //     ]
      //   },
      //   {
      //     id: 3, name: 'Checkout', icon: CreditCard, users: 31, color: '#eab308',
      //     fillColor: 'rgba(234, 179, 8, 0.15)',
      //     entering: 8, leaving: 7,
      //     sources: [
      //       { name: 'tiktok', count: 8 },
      //       { name: 'email', count: 7 },
      //       { name: 'bing', count: 6 },
      //       { name: 'twitter', count: 5 },
      //       { name: 'linkedin', count: 5 },
      //     ]
      //   },
      //   {
      //     id: 4, name: 'Purchase', icon: DollarSign, users: 117, color: '#22c55e',
      //     fillColor: 'rgba(34, 197, 94, 0.15)',
      //     entering: 5, leaving: 1,
      //     sources: [
      //       { name: 'google', count: 25 },
      //       { name: 'instagram', count: 22 },
      //       { name: 'twitter', count: 20 },
      //       { name: 'bing', count: 18 },
      //       { name: 'tiktok', count: 17 },
      //     ]
      //   },
      // ]
    }
    // Use real data from trafficReport.funnelData
    return trafficReport.funnelData
  }, [hasRealData, trafficReport])

  // Initialize stage data
  useEffect(() => {
    setStageData(initialStages)
  }, [initialStages])

  // Simulate live updates every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setStageData(prevStages => prevStages.map(stage => {
        // Random fluctuations
        const userChange = Math.floor(Math.random() * 7) - 3 // -3 to +3
        const newUsers = Math.max(10, stage.users + userChange)
        const entering = Math.max(0, Math.floor(Math.random() * 12))
        const leaving = Math.max(0, Math.floor(Math.random() * 10))

        // Shuffle source counts slightly
        const newSources = stage.sources.map(s => ({
          ...s,
          count: Math.max(1, s.count + Math.floor(Math.random() * 5) - 2)
        })).sort((a, b) => b.count - a.count)

        return {
          ...stage,
          users: newUsers,
          entering,
          leaving,
          sources: newSources,
        }
      }))
    }, 5000)

    return () => clearInterval(interval)
  }, [])

  // Filtered stage data based on selected source
  const filteredStageData = useMemo(() => {
    if (selectedSource === 'all') return stageData

    return stageData.map(stage => {
      // Find the count for the selected source in this stage
      const sourceData = stage.sources.find(s => s.name.toLowerCase() === selectedSource.toLowerCase())
      const filteredUsers = sourceData?.count || 0

      // Calculate proportional entering/leaving based on source ratio
      const totalUsers = stage.sources.reduce((sum, s) => sum + s.count, 0)
      const ratio = totalUsers > 0 ? filteredUsers / totalUsers : 0

      return {
        ...stage,
        users: filteredUsers,
        entering: Math.round(stage.entering * ratio),
        leaving: Math.round(stage.leaving * ratio),
        // Keep only the selected source in sources array
        sources: sourceData ? [sourceData] : [],
      }
    })
  }, [stageData, selectedSource])

  // Calculate max users for fill percentage - use filtered data
  const maxUsers = useMemo(() => Math.max(...filteredStageData.map(s => s.users), 10), [filteredStageData])

  // Calculate pass rates - use filtered data
  const passRates = filteredStageData.map((stage, index) => {
    if (index === 0) return null
    const prevUsers = filteredStageData[index - 1]?.users || 1
    const rate = prevUsers > 0 ? Math.round((stage.users / prevUsers) * 100) : 0
    return rate
  })

  // Source breakdown for selected stage - filtered by selectedSource
  const sourceBreakdown = useMemo(() => {
    if (selectedStage === null || !stageData[selectedStage]) return []
    const stage = stageData[selectedStage]

    // Get all sources with their counts
    const allSources = [
      { source: 'Email', count: 0, color: sourceColors.email },
      { source: 'Instagram', count: 0, color: sourceColors.instagram },
      { source: 'Google', count: 0, color: sourceColors.google },
      { source: 'Bing', count: 0, color: sourceColors.bing },
      { source: 'Tiktok', count: 0, color: sourceColors.tiktok },
      { source: 'Linkedin', count: 0, color: sourceColors.linkedin },
      { source: 'Twitter', count: 0, color: sourceColors.twitter },
      { source: 'Facebook', count: 0, color: sourceColors.facebook },
    ]

    // Update counts from stage data
    stage.sources.forEach(s => {
      const found = allSources.find(as => as.source.toLowerCase() === s.name.toLowerCase())
      if (found) found.count = s.count
    })

    // Filter by selected source if not "all"
    let filteredSources = allSources
    if (selectedSource !== 'all') {
      filteredSources = allSources.filter(s => s.source.toLowerCase() === selectedSource.toLowerCase())
    }

    const totalInStage = filteredSources.reduce((sum, s) => sum + s.count, 0)

    return filteredSources
      .map(s => ({
        ...s,
        percentage: totalInStage > 0 ? ((s.count / totalInStage) * 100).toFixed(1) : '0'
      }))
      .sort((a, b) => b.count - a.count)
  }, [selectedStage, stageData, selectedSource])

  // Selected user for modal
  const [selectedUser, setSelectedUser] = useState<FunnelUser | null>(null)

  // Sample products for cart items
  const sampleProducts = [
    { name: 'Running Shoes Elite', price: 189.99 },
    { name: 'Bluetooth Speaker Mini', price: 59.99 },
    { name: 'Wireless Earbuds Pro', price: 129.99 },
    { name: 'Fitness Tracker Band', price: 79.99 },
    { name: 'Laptop Stand Aluminum', price: 49.99 },
    { name: 'USB-C Hub 7-in-1', price: 39.99 },
    { name: 'Mechanical Keyboard', price: 149.99 },
    { name: 'Ergonomic Mouse', price: 69.99 },
  ]

  const campaigns = ['black_friday_promo', 'summer_sale', 'new_arrivals', 'holiday_deals', 'flash_sale']
  const terms = ['tech gadgets 2025', 'best deals', 'electronics sale', 'home office', 'fitness gear']
  const landingPages = [
    '/bundles/home-office-essentials',
    '/collections/tech-accessories',
    '/products/wireless-earbuds',
    '/collections/fitness-gear',
    '/bundles/work-from-home',
  ]

  // Sample users for each stage - filtered by selectedSource
  const sampleUsers: FunnelUser[] = useMemo(() => {
    const allSources = ['Instagram', 'Email', 'Bing', 'Linkedin', 'Direct', 'Facebook', 'Twitter', 'Google']
    const mediums = ['Email', 'Display', 'Organic', 'Social', 'Affiliate', 'Cpc', 'Banner', 'Referral', 'Video']
    const countries = ['JP', 'BR', 'DE', 'FR', 'CA', 'AU', 'GB', 'US']

    // If a source is selected, use only that source for all users
    const sourcesToUse = selectedSource === 'all'
      ? allSources
      : [selectedSource.charAt(0).toUpperCase() + selectedSource.slice(1)]

    return Array.from({ length: 8 }, (_, i) => {
      const numItems = Math.floor(Math.random() * 3) + 1
      const cartItems = Array.from({ length: numItems }, (_, j) => ({
        name: sampleProducts[(i + j) % sampleProducts.length].name,
        qty: 1,
        price: sampleProducts[(i + j) % sampleProducts.length].price,
      }))
      const cartValue = cartItems.reduce((sum, item) => sum + item.price * item.qty, 0)

      // Use the filtered source
      const userSource = sourcesToUse[i % sourcesToUse.length]

      return {
        id: `${1764860089149 + i * 45000}`,
        name: `Visitor ${1764860089149 + i * 45000}`,
        source: userSource,
        medium: mediums[i % mediums.length],
        country: countries[i % countries.length],
        cartValue: Math.round(cartValue * 100) / 100,
        stage: selectedStage || 2,
        utm: {
          source: userSource,
          medium: mediums[i % mediums.length].toLowerCase(),
          campaign: campaigns[i % campaigns.length],
          term: terms[i % terms.length],
          content: i % 3 === 0 ? null : `ad_variant_${i}`,
          id: `oJwfA6GdjhIVUG0RN1ieChGT${i}`,
        },
        landingPage: landingPages[i % landingPages.length],
        cartItems,
      }
    })
  }, [selectedStage, selectedSource])

  // Live stats - use filtered data
  const liveStats = useMemo(() => ({
    activeUsers: filteredStageData.reduce((sum, s) => sum + s.users, 0),
    convPerMin: filteredStageData.reduce((sum, s) => sum + s.entering, 0),
    dropPerMin: -filteredStageData.reduce((sum, s) => sum + s.leaving, 0),
    avgTime: '5m 12s',
  }), [filteredStageData])

  // Hide component if no real data
  if (!hasRealData || stageData.length === 0) {
    return null
  }

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-p-6">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <div className="tw-flex tw-items-center tw-gap-3">
          <h3 className="tw-text-lg tw-font-semibold tw-text-slate-900">Live Funnel Activity</h3>
          <span className="tw-flex tw-items-center tw-gap-1.5 tw-px-2 tw-py-0.5 tw-bg-green-100 tw-text-green-700 tw-rounded-full tw-text-xs tw-font-medium">
            <span className="tw-w-1.5 tw-h-1.5 tw-bg-green-500 tw-rounded-full tw-animate-pulse"></span>
            Live
          </span>
        </div>
        <div className="tw-flex tw-items-center tw-gap-6 tw-text-sm">
          <div className="tw-text-center">
            <p className="tw-text-slate-500">Active Users</p>
            <p className="tw-font-bold tw-text-slate-900">{liveStats.activeUsers}</p>
          </div>
          <div className="tw-text-center">
            <p className="tw-text-slate-500">Conv/min</p>
            <p className="tw-font-bold tw-text-green-600">+{liveStats.convPerMin}</p>
          </div>
          <div className="tw-text-center">
            <p className="tw-text-slate-500">Drop/min</p>
            <p className="tw-font-bold tw-text-red-600">{liveStats.dropPerMin}</p>
          </div>
          <div className="tw-text-center">
            <p className="tw-text-slate-500">Avg Time</p>
            <p className="tw-font-bold tw-text-blue-600">{liveStats.avgTime}</p>
          </div>
        </div>
      </div>

      <p className="tw-text-sm tw-text-slate-500 tw-mb-4">Click any stage to see traffic breakdown by source</p>

      {/* Source Filter Pills */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-6 tw-flex-wrap">
        <span className="tw-text-sm tw-text-slate-600">Filter by source:</span>
        {trafficSources.map((source) => (
          <button
            key={source.id}
            onClick={() => setSelectedSource(source.id)}
            className={`tw-px-3 tw-py-1.5 tw-rounded-full tw-text-sm tw-font-medium tw-transition-colors tw-flex tw-items-center tw-gap-1.5 ${
              selectedSource === source.id
                ? 'tw-bg-slate-900 tw-text-white'
                : 'tw-bg-slate-100 tw-text-slate-700 hover:tw-bg-slate-200'
            }`}
          >
            {source.color && (
              <span className="tw-w-2 tw-h-2 tw-rounded-full" style={{ backgroundColor: source.color }}></span>
            )}
            {source.name}
            <span className="tw-text-xs tw-opacity-70">({source.count})</span>
          </button>
        ))}
      </div>

      {/* Funnel Stages */}
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mb-6">
        {filteredStageData.map((stage, index) => {
          const Icon = stage.icon
          const isSelected = selectedStage === stage.id
          const fillPercent = Math.min((stage.users / maxUsers) * 100, 100)

          // Get sources with percentages for proportional dots
          // Use original stageData for calculating percentages when showing all sources
          const originalStage = stageData[index]
          const totalSourceUsers = originalStage?.sources.reduce((sum, s) => sum + s.count, 0) || 1
          const sourcesWithPercentage = (originalStage?.sources || [])
            .slice(0, 5)
            .map(s => ({
              ...s,
              percentage: (s.count / totalSourceUsers) * 100
            }))

          return (
            <React.Fragment key={stage.id}>
              <div className="tw-flex-1 tw-flex tw-flex-col tw-items-center">
                {/* Stage Card */}
                <button
                  onClick={() => setSelectedStage(isSelected ? null : stage.id)}
                  className={`tw-w-full tw-rounded-xl tw-border-2 tw-transition-all tw-cursor-pointer tw-relative tw-overflow-hidden ${
                    isSelected
                      ? 'tw-border-blue-500 tw-shadow-lg tw-scale-105'
                      : 'tw-border-slate-200 hover:tw-shadow-md'
                  }`}
                  style={{ minHeight: '180px' }}
                >
                  {/* Fill background from bottom */}
                  <div
                    className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-transition-all tw-duration-500"
                    style={{
                      height: `${fillPercent}%`,
                      backgroundColor: stage.fillColor,
                    }}
                  />

                  {/* Delta badges */}
                  {stage.leaving > 0 && (
                    <div className="tw-absolute tw-top-2 tw-left-2 tw-px-1.5 tw-py-0.5 tw-bg-red-500 tw-text-white tw-text-xs tw-font-bold tw-rounded">
                      -{stage.leaving}
                    </div>
                  )}
                  {stage.entering > 0 && (
                    <div className="tw-absolute tw-top-2 tw-right-2 tw-px-1.5 tw-py-0.5 tw-bg-green-500 tw-text-white tw-text-xs tw-font-bold tw-rounded">
                      +{stage.entering}
                    </div>
                  )}

                  {/* Content */}
                  <div className="tw-relative tw-z-10 tw-p-4 tw-flex tw-flex-col tw-items-center">
                    <div className="tw-p-2 tw-rounded-lg tw-mb-2 tw-bg-white/80" style={{ boxShadow: `0 0 0 1px ${stage.color}30` }}>
                      <Icon className="tw-w-5 tw-h-5" style={{ color: stage.color }} />
                    </div>
                    <p className="tw-text-sm tw-font-medium tw-text-slate-700">{stage.name}</p>
                    <p className="tw-text-3xl tw-font-bold tw-mt-1" style={{ color: stage.color }}>{stage.users}</p>
                    <p className="tw-text-xs tw-text-slate-500">users</p>

                    {/* Source indicator - proportional dots or full bar when filtered */}
                    <div className="tw-mt-3 tw-w-full tw-px-2">
                      {selectedSource === 'all' ? (
                        // Show proportional dots when all sources selected
                        <div className="tw-flex tw-justify-center tw-gap-0.5">
                          {sourcesWithPercentage.map((source, i) => {
                            // Size dots based on percentage (min 6px, max 14px)
                            const size = Math.max(6, Math.min(14, 6 + (source.percentage / 100) * 8))
                            return (
                              <div
                                key={i}
                                className="tw-rounded-full tw-transition-all"
                                style={{
                                  backgroundColor: sourceColors[source.name.toLowerCase()] || '#94a3b8',
                                  width: `${size}px`,
                                  height: `${size}px`,
                                }}
                                title={`${source.name}: ${source.count} (${source.percentage.toFixed(1)}%)`}
                              />
                            )
                          })}
                        </div>
                      ) : (
                        // Show full bar when a specific source is filtered
                        <div
                          className="tw-h-2 tw-rounded-full tw-w-full"
                          style={{ backgroundColor: sourceColors[selectedSource] || '#94a3b8' }}
                          title={`${selectedSource}: 100%`}
                        />
                      )}
                    </div>
                  </div>
                </button>

                {/* Pass Rate */}
                {passRates[index] !== null && (
                  <div className="tw-mt-3 tw-text-center">
                    <p className="tw-text-lg tw-font-bold tw-text-slate-900">{passRates[index]}%</p>
                    <div className="tw-flex tw-items-center tw-gap-1 tw-justify-center">
                      <p className="tw-text-xs tw-text-slate-500">pass rate</p>
                      <TrendingUp className="tw-w-3 tw-h-3 tw-text-green-500" />
                    </div>
                  </div>
                )}
              </div>

              {/* Arrow between stages */}
              {index < filteredStageData.length - 1 && (
                <div className="tw-flex tw-items-center tw-pt-16">
                  <ArrowRight className="tw-w-5 tw-h-5 tw-text-slate-300" />
                </div>
              )}
            </React.Fragment>
          )
        })}
      </div>

      {/* Stage Detail Panel */}
      {selectedStage !== null && filteredStageData[selectedStage] && (
        <div className="tw-bg-slate-50 tw-rounded-xl tw-p-6 tw-relative">
          <button
            onClick={() => setSelectedStage(null)}
            className="tw-absolute tw-top-4 tw-right-4 tw-p-1 tw-rounded-full hover:tw-bg-slate-200"
          >
            <X className="tw-w-5 tw-h-5 tw-text-slate-400" />
          </button>

          <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
            {React.createElement(filteredStageData[selectedStage].icon, {
              className: 'tw-w-6 tw-h-6',
              style: { color: filteredStageData[selectedStage].color }
            })}
            <div>
              <h4 className="tw-font-semibold tw-text-slate-900">{filteredStageData[selectedStage].name} Stage</h4>
              <p className="tw-text-sm tw-text-slate-500">{filteredStageData[selectedStage].users} users currently</p>
            </div>
          </div>

          {/* Traffic Sources Grid */}
          <div className="tw-mb-6">
            <h5 className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-mb-3">Traffic Sources</h5>
            <div className="tw-grid tw-grid-cols-4 tw-gap-3">
              {sourceBreakdown.map((source) => (
                <div key={source.source} className="tw-bg-white tw-rounded-lg tw-p-3 tw-border tw-border-slate-200">
                  <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                    <span className="tw-w-2.5 tw-h-2.5 tw-rounded-full" style={{ backgroundColor: source.color }}></span>
                    <span className="tw-text-sm tw-font-medium tw-text-slate-700">{source.source}</span>
                  </div>
                  <p className="tw-text-xl tw-font-bold tw-text-slate-900">{source.count}</p>
                  <p className="tw-text-xs tw-text-slate-500">{source.percentage}%</p>
                </div>
              ))}
            </div>
          </div>

          {/* Sample Users */}
          <div>
            <h5 className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-mb-3">Sample Users</h5>
            <div className="tw-grid tw-grid-cols-4 tw-gap-3">
              {sampleUsers.filter(u => u.stage === selectedStage || selectedStage === 2).slice(0, 8).map((user) => (
                <button
                  key={user.id}
                  onClick={() => setSelectedUser(user)}
                  className="tw-bg-white tw-rounded-lg tw-p-3 tw-border tw-border-slate-200 tw-flex tw-items-center tw-gap-3 tw-text-left hover:tw-border-blue-300 hover:tw-shadow-md tw-transition-all tw-cursor-pointer"
                >
                  <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-gradient-to-br tw-from-blue-400 tw-to-blue-600 tw-flex tw-items-center tw-justify-center tw-text-white tw-font-semibold tw-text-sm">
                    <Users className="tw-w-5 tw-h-5" />
                  </div>
                  <div className="tw-flex-1 tw-min-w-0">
                    <p className="tw-font-medium tw-text-slate-900 tw-text-sm">{user.name}</p>
                    <p className="tw-text-xs tw-text-slate-500 tw-truncate">{user.source} / {user.medium}</p>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mt-1">
                      <span className="tw-text-xs tw-font-medium tw-text-slate-600 tw-bg-slate-100 tw-px-1.5 tw-py-0.5 tw-rounded">{user.country}</span>
                      <span className="tw-text-sm tw-font-semibold tw-text-green-600">{formatCurrency(user.cartValue, currency)}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4" onClick={() => setSelectedUser(null)}>
          <div className="tw-absolute tw-inset-0 tw-bg-black/50" />
          <div
            className="tw-relative tw-bg-white tw-rounded-xl tw-shadow-2xl tw-max-w-md tw-w-full tw-max-h-[90vh] tw-overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="tw-sticky tw-top-0 tw-bg-white tw-px-6 tw-pt-6 tw-pb-4 tw-border-b tw-border-slate-100">
              <button
                onClick={() => setSelectedUser(null)}
                className="tw-absolute tw-top-4 tw-right-4 tw-p-1.5 tw-rounded-full hover:tw-bg-slate-100 tw-transition-colors"
              >
                <X className="tw-w-5 tw-h-5 tw-text-slate-400" />
              </button>
              <div className="tw-flex tw-items-center tw-gap-3">
                <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gradient-to-br tw-from-blue-400 tw-to-blue-600 tw-flex tw-items-center tw-justify-center tw-text-white">
                  <Users className="tw-w-6 tw-h-6" />
                </div>
                <div>
                  <h3 className="tw-text-lg tw-font-semibold tw-text-slate-900">{selectedUser.name}</h3>
                  <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-bg-orange-100 tw-text-orange-600 tw-text-xs tw-font-medium tw-rounded">
                    {filteredStageData[selectedStage || 0]?.name || 'Cart'}
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="tw-px-6 tw-py-4 tw-space-y-5">
              {/* UTM Parameters */}
              <div>
                <h4 className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-mb-3">UTM Parameters</h4>
                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                  <div className="tw-bg-slate-50 tw-rounded-lg tw-p-3">
                    <p className="tw-text-xs tw-text-slate-500 tw-mb-1">utm_source</p>
                    <p className="tw-font-medium tw-text-slate-900">{selectedUser.utm.source}</p>
                  </div>
                  <div className="tw-bg-slate-50 tw-rounded-lg tw-p-3">
                    <p className="tw-text-xs tw-text-slate-500 tw-mb-1">utm_medium</p>
                    <p className="tw-font-medium tw-text-slate-900">{selectedUser.utm.medium}</p>
                  </div>
                  <div className="tw-col-span-2 tw-bg-slate-50 tw-rounded-lg tw-p-3">
                    <p className="tw-text-xs tw-text-slate-500 tw-mb-1">utm_campaign</p>
                    <p className="tw-font-medium tw-text-slate-900">{selectedUser.utm.campaign}</p>
                  </div>
                  <div className="tw-col-span-2 tw-bg-slate-50 tw-rounded-lg tw-p-3">
                    <p className="tw-text-xs tw-text-slate-500 tw-mb-1">utm_term</p>
                    <p className="tw-font-medium tw-text-slate-900">{selectedUser.utm.term}</p>
                  </div>
                  <div className="tw-col-span-2 tw-bg-slate-50 tw-rounded-lg tw-p-3">
                    <p className="tw-text-xs tw-text-slate-500 tw-mb-1">utm_content</p>
                    <p className={`tw-font-medium ${selectedUser.utm.content ? 'tw-text-slate-900' : 'tw-text-slate-400 tw-italic'}`}>
                      {selectedUser.utm.content || 'Not set'}
                    </p>
                  </div>
                  <div className="tw-col-span-2 tw-bg-slate-50 tw-rounded-lg tw-p-3">
                    <p className="tw-text-xs tw-text-slate-500 tw-mb-1">utm_id</p>
                    <p className="tw-font-medium tw-text-slate-900 tw-font-mono tw-text-sm">{selectedUser.utm.id}</p>
                  </div>
                </div>
              </div>

              {/* Landing Page */}
              <div>
                <h4 className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-mb-2">Landing Page</h4>
                <a href="#" className="tw-text-blue-600 hover:tw-underline tw-font-medium tw-font-mono tw-text-sm">
                  {selectedUser.landingPage}
                </a>
              </div>

              {/* Location */}
              <div>
                <h4 className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-mb-2">Location</h4>
                <div className="tw-flex tw-items-center tw-gap-2">
                  <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-6 tw-bg-slate-100 tw-rounded tw-text-xs tw-font-bold tw-text-slate-600">
                    {selectedUser.country}
                  </span>
                  <span className="tw-text-slate-900">
                    {selectedUser.country === 'US' && 'United States'}
                    {selectedUser.country === 'CA' && 'Canada'}
                    {selectedUser.country === 'GB' && 'United Kingdom'}
                    {selectedUser.country === 'DE' && 'Germany'}
                    {selectedUser.country === 'FR' && 'France'}
                    {selectedUser.country === 'JP' && 'Japan'}
                    {selectedUser.country === 'BR' && 'Brazil'}
                    {selectedUser.country === 'AU' && 'Australia'}
                  </span>
                </div>
              </div>

              {/* Cart Items */}
              <div>
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                  <h4 className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wider">Cart Items</h4>
                  <span className="tw-text-lg tw-font-bold tw-text-green-600">{formatCurrency(selectedUser.cartValue, currency)}</span>
                </div>
                <div className="tw-space-y-2">
                  {selectedUser.cartItems.map((item, index) => (
                    <div key={index} className="tw-flex tw-items-center tw-justify-between tw-py-2 tw-border-b tw-border-slate-100 last:tw-border-0">
                      <div>
                        <p className="tw-font-medium tw-text-slate-900">{item.name}</p>
                        <p className="tw-text-xs tw-text-slate-500">Qty: {item.qty}</p>
                      </div>
                      <span className="tw-font-semibold tw-text-slate-700">{formatCurrency(item.price, currency)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="tw-flex tw-items-center tw-justify-between tw-mt-4 tw-pt-4 tw-border-t tw-border-slate-200">
        <p className="tw-text-xs tw-text-slate-500">Updates every 5 seconds while in view</p>
        <p className="tw-text-xs tw-text-slate-500">Showing aggregated view for {liveStats.activeUsers} concurrent users</p>
      </div>
    </div>
  )
}

// Types for Live Funnel
interface FunnelUser {
  id: string
  name: string
  source: string
  medium: string
  country: string
  cartValue: number
  stage: number
  utm: {
    source: string
    medium: string
    campaign: string
    term: string
    content: string | null
    id: string
  }
  landingPage: string
  cartItems: { name: string; qty: number; price: number }[]
}

interface StageData {
  id: number
  name: string
  icon: React.ElementType
  users: number
  color: string
  fillColor: string
  entering: number
  leaving: number
  sources: { name: string; count: number }[]
}
